import { useState } from "react";
import { ArrowUpDown, Check, ChevronDown } from "lucide-react";
import { SortDirections, SortQuery } from "~/modules/filters/types";
import { AssetColumns } from "~/modules/assets/types";
import { FilterFormSortField } from "~/modules/filters/components/FilterFormContext";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { FilterLabel } from "~/modules/asset_screener/components/FilterLabel";
import { FilterControlButton } from "~/modules/asset_screener/components/FilterControlButton";
import { DATA_ROOM_SORT_OPTION_VALUE } from "../../consts";
import { trackFilterSortBy } from "../../analytics";
import { FILTER_CONTROL_MUTED_ICON_CLASS } from "../AssetScreenerFilters/styles";

// // // //

interface SortOption {
    value: string;
    label: string;
    sort: SortQuery;
}

const SORT_OPTIONS: SortOption[] = [
    {
        value: DATA_ROOM_SORT_OPTION_VALUE,
        label: "Data Room Available",
        sort: {
            field: AssetColumns.bridgedTokenValueDollar,
            direction: SortDirections.desc,
        },
    },
    {
        value: "apy-30-day-desc",
        label: "Highest 30D APY",
        sort: { field: AssetColumns.apy30Day, direction: SortDirections.desc },
    },
    {
        value: "apy-30-day-asc",
        label: "Lowest 30D APY",
        sort: { field: AssetColumns.apy30Day, direction: SortDirections.asc },
    },
    {
        value: "return-1y-desc",
        label: "Highest 1Y Return",
        sort: {
            field: AssetColumns.trailing1YearReturnPercent,
            direction: SortDirections.desc,
        },
    },
    {
        value: "return-1m-desc",
        label: "Highest 1M Return",
        sort: {
            field: AssetColumns.trailing1MonthReturnPercent,
            direction: SortDirections.desc,
        },
    },
    {
        value: "aum-desc",
        label: "Highest AUM",
        sort: {
            field: AssetColumns.bridgedTokenValueDollar,
            direction: SortDirections.desc,
        },
    },
    {
        value: "aum-asc",
        label: "Lowest AUM",
        sort: {
            field: AssetColumns.bridgedTokenValueDollar,
            direction: SortDirections.asc,
        },
    },
];

/**
 * AssetScreenerSortInput
 * Popover picker for the asset screener's preset sort options. Writes the
 * selected option's sort to the surrounding FilterFormContextProvider, and
 * reports the option value separately since "Data Room Available" and
 * "Highest AUM" share the same API sort.
 */
export function AssetScreenerSortInput(props: {
    selectedOptionValue: string | null;
    onSelectedOptionValueChange: (value: string) => void;
    showLabel?: boolean;
}) {
    const {
        selectedOptionValue,
        onSelectedOptionValueChange,
        showLabel = true,
    } = props;
    const [open, setOpen] = useState(false);

    const selectedOption = SORT_OPTIONS.find(
        (option) => option.value === selectedOptionValue,
    );

    return (
        <FilterFormSortField>
            {({ onChange }) => {
                return (
                    <div className={cn({ "space-y-1": showLabel })}>
                        {showLabel && <FilterLabel label="Sort By" />}
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <FilterControlButton
                                    role="combobox"
                                    aria-expanded={open}
                                    aria-label="Select sort order"
                                >
                                    <div className="flex items-center">
                                        <ArrowUpDown
                                            className={`${FILTER_CONTROL_MUTED_ICON_CLASS} mr-1.5 h-3.5 w-3.5`}
                                        />
                                        <span className="font-normal">
                                            {selectedOption?.label ??
                                                "Sort by..."}
                                        </span>
                                    </div>
                                    <ChevronDown
                                        className={`${FILTER_CONTROL_MUTED_ICON_CLASS} h-3.5 w-3.5`}
                                    />
                                </FilterControlButton>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-52 p-1 rounded-lg shadow-xs"
                                align="start"
                                onOpenAutoFocus={(e) => e.preventDefault()}
                            >
                                <div className="flex flex-col">
                                    {SORT_OPTIONS.map((option) => {
                                        const isSelected =
                                            selectedOption?.value ===
                                            option.value;

                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => {
                                                    trackFilterSortBy(
                                                        option.value,
                                                    );
                                                    onChange(option.sort);
                                                    onSelectedOptionValueChange(
                                                        option.value,
                                                    );
                                                    setOpen(false);
                                                }}
                                                className={cn(
                                                    "flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors",
                                                    "hover:bg-muted focus:bg-muted focus:outline-none",
                                                    {
                                                        "bg-tertiary":
                                                            isSelected,
                                                    },
                                                )}
                                                tabIndex={0}
                                                aria-selected={isSelected}
                                            >
                                                <span>{option.label}</span>
                                                {isSelected && (
                                                    <Check className="h-3.5 w-3.5 text-secondary-foreground" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                );
            }}
        </FilterFormSortField>
    );
}
